import { Backdrop, Box, Container, Typography, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAccountState } from 'contexts/account/account-context';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { FormattedMessage } from 'react-intl';
import { MarketId } from 'types/market-id.enum';
import SidebarOverlay from './components/sidebar-overlay';
import Toolbar from './components/toolbar';

const PREFIX = 'Layout';
const classes = {
  drawerPaper: `${PREFIX}-drawerPaper`,
};

const Main = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexGrow: 1,
  height: '100%',
  width: '100%',
  backgroundColor: theme.palette.background.main,
}));

const Wrapper = styled('div', { label: 'Layout-Wrapper' })<{ isOverview: boolean }>(({ theme, isOverview }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  justifyContent: 'flex-start',
  overflow: 'auto',
}));

const Content = styled('main')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  padding: theme.spacing(0, 3),

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(0, 10),
  },
}));

const StyledContainer = styled(Container)(() => ({
  flexGrow: 1,
  alignItems: 'flex-end',
}));

const Footer = styled('footer')(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(20, 0, 10),
}));

type LayoutState = {
  currentNavName: string;
  handleSidebarToggle: () => void;
  sidebarOpen: boolean;
};

const LayoutContext = createContext<LayoutState | undefined>(undefined);

type LayoutProps = {
  children: React.ReactNode;
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { account } = useAccountState();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentNavName, setCurrentNavName] = useState('');

  const isOverview = location.pathname.split('/')[1] === 'overview';
  const isDeliveryBoard = location.pathname.split('/')[1] === 'delivery-board';
  const isOverlayRoute = isOverview || isDeliveryBoard;

  const handleSidebarToggle = () => {
    setSidebarOpen((open) => !open);
  };

  useEffect(() => {
    setCurrentNavName(location.pathname.split('/')[1]);
  }, [location, mobile]);

  useEffect(() => {
    wrapperRef.current?.scrollTo(0, 0);

    if (isOverlayRoute) {
      setSidebarOpen(false);
    }
  }, [isOverlayRoute, location.pathname]);

  return (
    <LayoutContext.Provider value={{ currentNavName, handleSidebarToggle, sidebarOpen }}>
      <Main>
        <Box display={{ xs: 'block', md: 'none' }}>
          <Backdrop open={sidebarOpen} onClick={handleSidebarToggle} sx={{ zIndex: 20 }}>
            <SidebarOverlay />
          </Backdrop>
        </Box>
        <Box display={{ xs: 'none', md: 'block' }}>
          <SidebarOverlay />
        </Box>
        <Wrapper ref={wrapperRef} isOverview={isOverview}>
          <Toolbar handleDrawerToggle={handleSidebarToggle} />
          <StyledContainer disableGutters maxWidth={isDeliveryBoard ? false : 'xl'}>
            <Content>{children}</Content>
          </StyledContainer>
          <StyledContainer disableGutters sx={{ display: 'flex', justifyContent: 'center' }}>
            <Footer>
              <Typography color={theme.palette.common.white}>
                {account.company?.marketId === MarketId.UnitedKingdom ? (
                  <Box display={'flex'} flexDirection={'column'} gap={2}>
                    <Box>
                      <FormattedMessage id={`footer.copyrightUk`} />
                    </Box>
                    <Box>
                      <FormattedMessage id={`footer.ukAddress`} />
                    </Box>
                    <Box>
                      <FormattedMessage id={`footer.ukRegister`} />
                    </Box>
                  </Box>
                ) : (
                  <FormattedMessage id={`footer.copyright`} />
                )}
              </Typography>
              <Typography color={theme.palette.common.white}>
                <span id="teconsent" style={{ margin: 10, textDecoration: 'underline' }}></span>
                <span id="trustarc-irm-placeholder" style={{ marginLeft: 5 }}></span>
              </Typography>
            </Footer>
          </StyledContainer>
        </Wrapper>
      </Main>
    </LayoutContext.Provider>
  );
};

export function useLayoutState() {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayoutState must be used within a Layout');
  }
  return context;
}

export default Layout;
